import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core"
import { VisibilityOffOutlined, VisibilityOutlined } from "@material-ui/icons"
import AppContext from "contexts/App"
import { Form, Formik } from "formik"
import { useContext, useRef, useState } from "react"
import * as Yup from "yup"

import LoaderButton from "../../../components/LocalLoadingButton"
import useStyles from "./styles"

const PasswordResetForm = () => {
  const classes = useStyles()
  const { dialogs, updatePasswordMutation, openSnackBar } =
    useContext(AppContext)
  const thisDialog = dialogs?.["clerkMyAccount"] || {}
  const { open = false, data = {} } = thisDialog

  const formRef = useRef()
  const [passwordVisibility, setPasswordVisibility] = useState(false)
  const [newPasswordVisibility, setNewPasswordVisibility] = useState(false)
  const [confirmedNewPasswordVisibility, setConfirmedNewPasswordVisibility] =
    useState(false)

  const schema = Yup.object().shape({
    currentPassword: Yup.string().required("Required"),
    newPassword: Yup.string().when("currentPassword", {
      is: (val) => val?.length > 0,
      then: Yup.string()
        .required("Must type new password")
        .matches(
          /^(?=.*[\d])(?=.*[!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/])(?=.*[A-Z])[A-Za-z\d!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/]{8,}$/,
          "Requirements: 8 characters, 1 Upper Case & 1 special character"
        ),
      otherwise: Yup.string().min(0),
    }),
    confirmPassword: Yup.string()
      .when("currentPassword", {
        is: (val) => val?.length > 0,
        then: Yup.string().required("Must confirm new password"),
        otherwise: Yup.string().min(0),
      })
      .when("newPassword", {
        is: (val) => val?.length > 0,
        then: Yup.string()
          .oneOf([Yup.ref("newPassword")], "Both passwords must match")
          .required("Must confirm new password"),
      }),
  })

  const onSubmit = (values, actions) => {
    const data = {
      current_password: values.currentPassword,
      password: values.newPassword,
    }
    return new Promise((resolve, reject) => {
      updatePasswordMutation
        .mutateAsync(data)
        .then((res) => {
          openSnackBar({
            message: "Password successfully updated!",
          })
          actions.resetForm()
          resolve()
        })
        .catch((err, res) => {
          openSnackBar({
            message: "Error attempting to update your password",
          })
          reject()
        })
    })
  }

  return (
    <Formik
      initialValues={{
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }}
      onSubmit={onSubmit}
      validationSchema={schema}
      innerRef={formRef}
    >
      {({
        dirty,
        handleChange,
        values,
        errors,
        touched,
        handleBlur,
        isSubmitting,
        handleSubmit,
        setFieldValue,
      }) => (
        <Form onSubmit={handleSubmit} className={classes.formContainer}>
          <Typography variant="h5" className={classes.contentTitles}>
            Password Reset
          </Typography>
          <Box display="flex" justifyContent="flex-start">
            <Box display="flex" flexWrap="wrap">
              <TextField
                name="currentPassword"
                type={!passwordVisibility ? "password" : "text"}
                label="Current Password"
                variant="outlined"
                value={values?.currentPassword}
                className={classes.textfield}
                onChange={handleChange}
                helperText={touched.currentPassword && errors.currentPassword}
                error={touched.currentPassword && errors.currentPassword}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() =>
                          setPasswordVisibility(!passwordVisibility)
                        }
                      >
                        {passwordVisibility ? (
                          <VisibilityOffOutlined
                            fontSize="small"
                            style={{ color: "#898989" }}
                          />
                        ) : (
                          <VisibilityOutlined
                            fontSize="small"
                            style={{ color: "#898989" }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                name="newPassword"
                label="New Password"
                type={!newPasswordVisibility ? "password" : "text"}
                variant="outlined"
                value={values?.newPassword}
                className={classes.textfield}
                onChange={handleChange}
                helperText={touched.newPassword && errors.newPassword}
                error={touched.newPassword && errors.newPassword}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() =>
                          setNewPasswordVisibility(!newPasswordVisibility)
                        }
                      >
                        {newPasswordVisibility ? (
                          <VisibilityOffOutlined
                            fontSize="small"
                            style={{ color: "#898989" }}
                          />
                        ) : (
                          <VisibilityOutlined
                            fontSize="small"
                            style={{ color: "#898989" }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                name="confirmPassword"
                type={!confirmedNewPasswordVisibility ? "password" : "text"}
                label="Confirm New Password"
                variant="outlined"
                value={values?.confirmPassword}
                className={classes.textfield}
                onChange={handleChange}
                helperText={touched.confirmPassword && errors.confirmPassword}
                error={touched.confirmPassword && errors.confirmPassword}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() =>
                          setConfirmedNewPasswordVisibility(
                            !confirmedNewPasswordVisibility
                          )
                        }
                      >
                        {confirmedNewPasswordVisibility ? (
                          <VisibilityOffOutlined
                            fontSize="small"
                            style={{ color: "#898989" }}
                          />
                        ) : (
                          <VisibilityOutlined
                            fontSize="small"
                            style={{ color: "#898989" }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <Box display="flex" justifyContent="center" marginTop="30px">
            <LoaderButton
              color="primary"
              variant="contained"
              type="submit"
              className={classes.saveButton}
              classoverrides={{
                wrapper: classes.button,
                button: {
                  root: classes.buttonRoot,
                },
              }}
              disabled={!dirty}
              working={isSubmitting}
            >
              Update Password
            </LoaderButton>
          </Box>
        </Form>
      )}
    </Formik>
  )
}
export default PasswordResetForm
